import {Directive, Injectable, Injector} from '@angular/core';
import {MatDialogRef} from '@angular/material/dialog';
import type {Api} from '@im-ng/api';
import type {DbRestoreComponent} from '@im-ng/db/restore';
import {DynamicForm} from '@im-ng/form';
import {TextBase} from '@im-ng/text';
import {DB_RESTORE} from '@im-ng/token/db';
import {DialogService} from '@im-ng/ui/dialog';
import {notPermitted, UserPermissionService} from '@im-ng/user';
import {HttpRequester} from '@js-dot/api/http';
import {DotCoreService} from '@ng-dot/core';
import {DotFormInterface} from '@ng-dot/form';
import {DotRouteService} from '@ng-dot/route';
import {DotTranslateService} from '@ng-dot/text';
import {DotModalService} from '@ng-dot/ui/modal';
import {DotToastService} from '@ng-dot/ui/toast';
import {Observable, Subject, throwError} from 'rxjs';
import {tap} from 'rxjs/operators';
import {ContentDownloadService} from '../services/content-download.service';
import {LayoutService} from '../services/layout.service';

/**
 * CRUD 자동화 API 설정 구조
 */
interface ControlApiService {
	/**
	 * 단일 등록 API
	 */
	$create?: Api<any, any, any> | HttpRequester;
	/**
	 * 단일 읽기 API
	 */
	$read?: Api<any, any, any> | HttpRequester;
	/**
	 * 다중 읽기 API
	 */
	$reads?: Api<any, any, any> | HttpRequester;
	/**
	 * 단일 수정 API
	 */
	$update?: Api<any, any, any> | HttpRequester;
	/**
	 * 다중 수정 API
	 */
	$updates?: Api<any, any, any> | HttpRequester;
	/**
	 * 단일 삭제 API
	 */
	$delete?: Api<any, any, any> | HttpRequester;
	/**
	 * 다중 삭제 API
	 */
	$deletes?: Api<any, any, any> | HttpRequester;

	/**
	 * 초기화 API
	 */
	$reset?: Api<any, any, any> | HttpRequester;
}

/**
 * CRUD 자동화 설정 구조 기반
 */
interface ControlOptionBase {
	/**
	 * 동적 폼
	 */
	form?: DotFormInterface;
	/**
	 * 기본 주소 인자
	 */
	params?: {};
	/**
	 * 주소 인자에 할당될 기본 값 속성명
	 * @default seq
	 */
	primaryKeyName?: string;
	/**
	 * 주소 인자에 대입될 주소 인자명
	 */
	paramsName?: string;
	/**
	 * 대화상자 실행전 실행 함수
	 */
	beforeDialog?: Function;
	/**
	 * 권한 경로 앞머리
	 */
	permissionPrefix?: string;
	/**
	 * 다이얼로그 ID
	 */
	dialogId?: string;
}

/**
 * CRUD 자동화 설정, API 최상위 대입 구조
 */
interface ControlOptionSpreadApi extends ControlOptionBase {
	/**
	 * 단일 등록 API
	 */
	$create?: Api<any, any, any> | HttpRequester;
	/**
	 * 단일 읽기 API
	 */
	$read?: Api<any, any, any> | HttpRequester;
	/**
	 * 다중 읽기 API
	 */
	$reads?: Api<any, any, any> | HttpRequester;
	/**
	 * 단일 수정 API
	 */
	$update?: Api<any, any, any> | HttpRequester;
	/**
	 * 다중 수정 API
	 */
	$updates?: Api<any, any, any> | HttpRequester;
	/**
	 * 단일 삭제 API
	 */
	$delete?: Api<any, any, any> | HttpRequester;
	/**
	 * 다중 삭제 API
	 */
	$deletes?: Api<any, any, any> | HttpRequester;

	/**
	 * 초기화 API
	 */
	$reset?: Api<any, any, any> | HttpRequester;
}

/**
 * CRUD 자동화 설정, API 서비스 대입 구조
 */
interface ControlOptionService extends ControlOptionBase {
	/**
	 * HTTP 서비스
	 */
	service: ControlApiService;
}

/**
 * CRUD 자동화 설정 유형
 */
type ControlOption =
	| ControlOptionSpreadApi
	| ControlOptionService;

/**
 * CRUD 자동화, 삭제 인자 유형
 */
export type ControlDeleteParams = string | number | Record<string, string | number>;

/**
 * CRUD 자동화 처리 누락 오류 문구
 */
const $withErrorMessage = `'ControlOperator.with()' 가 실행 되지 않았습니다.`;

/**
 * CRUD Control 문자열
 *
 * @inheritDoc
 * @see TextBase
 * @deprecated LazyPhraseService 사용
 */
@Injectable({
	providedIn: 'root'
})
export class ControlText extends TextBase {
	override readonly en = {
		created: 'Successful Created',
		updated: 'Successful Updated',
		deleted: 'Successful Deleted',
		deletedAll: 'Successful Deleted All'
	};

	override readonly kr = {
		created: '생성 했어요.',
		updated: '수정 했어요.',
		deleted: '삭제 했어요.',
		deletedAll: '모두 삭제 했어요.'
	};
}

@Directive()
export class ControlOperator {
	protected _$create!: Api<any, any> | HttpRequester;
	protected _$read!: Api<any, any> | HttpRequester;
	protected _$reads!: Api<any, any> | HttpRequester;
	protected _$update!: Api<any, any> | HttpRequester;

	protected _$delete!: Api<any, any> | HttpRequester;
	protected _$deletes!: Api<any, any> | HttpRequester;

	protected _core: DotCoreService;
	protected _layout: LayoutService;
	protected _dialog: DialogService;
	protected _toast: DotToastService;
	protected _permission: UserPermissionService;
	protected _contentDownload: ContentDownloadService;
	protected _modal: DotModalService;
	//////////////////////////////
	// customizable option
	protected _translate: DotTranslateService;
	protected _text: ControlText;
	protected _route: DotRouteService;
	protected _selfErrorCatchFunction = this._errorCatchFunction.bind(this);
	//////////////////////////////
	protected _form!: DynamicForm;
	protected _form2!: DynamicForm;
	protected _params!: {};

	protected _paramsName = 'seq';
	protected _dialogId: string;
	protected _beforeDialog!: Function;

	//////////////////////////////
	protected _selfCreateAction = this._createAction.bind(this);

	protected _feature!: string;

	$updates!: Api<any, any> | HttpRequester;

	_primaryKeyName!: string;
	paramsName!: string;

	public _permissionPrefix!: string;

	//////////////////////////////
	// self binding methods
	changed$ = new Subject<void>();


	constructor(protected _injector: Injector) {
		this._layout          = _injector.get(LayoutService);
		this._dialog          = _injector.get(DialogService);
		this._route           = _injector.get(DotRouteService);
		this._permission      = _injector.get(UserPermissionService);
		this._toast           = _injector.get(DotToastService);
		this._text            = _injector.get(ControlText);
		this._translate       = _injector.get(DotTranslateService);
		this._contentDownload = _injector.get(ContentDownloadService);
		this._modal           = _injector.get(DotModalService);
		this._core            = _injector.get(DotCoreService);
	}

	protected _getApi(type: string);
	protected _getApi(type) {
		const api = this[type];
		if (!api) {
			throw new Error(`Does not defined ${type} API`);
		}
		return api;
	}

	protected _getParams(params?: {});
	protected _getParams(params?) {
		const {_params} = this;
		const defaults  = _params ? {..._params} : {};

		for (let key in defaults) {
			if (!defaults.hasOwnProperty(key)) {
				continue;
			}
			const param = defaults[key];
			if (typeof param === 'function') {
				defaults[key] = param();
			}
		}

		const {_primaryKeyName, _paramsName} = this;

		params = params ?
			{
				...defaults,
				...(
					typeof params === 'object' ?
						params :
						{
							[_paramsName]: params
						}
				)
			} :
			defaults;

		if (_paramsName !== 'seq' && params[_primaryKeyName]) {
			params[_paramsName] = params[_primaryKeyName];
		}

		return params;
	}

	protected _permit(type: 'write' | 'delete') {
		const {_permissionPrefix} = this;

		if (!_permissionPrefix) {
			return true;
		}

		if (this._permission.permit(_permissionPrefix + '/' + type)) {
			return true;
		}

		console.error(notPermitted(this, type));
		return false;
	}

	protected _errorCatchFunction() {
		this._form.freeze(false);
	}

	//////////////////////////////
	// DIALOG

	protected _onBeforeDialog() {
		if (this._beforeDialog) {
			this._beforeDialog.apply(this);
		}
	}

	protected _createAction(dialogRef: MatDialogRef<any>, values: {}) {
		this._form.freeze();

		this.creating(values)
			.subscribe({
				next: () => {
					dialogRef.close();
					this._toast.medium({
							id: 'created',
							body: this._text.get('created'),
							icon: 'icon24:check'
						}
					);
					this.changed();
				},
				error: () => {
					dialogRef.close();
					this.changed();
					return this._selfErrorCatchFunction;
				}
			});
	}

	creating(values) {
		return this._getApi('_$create').request(this._getParams(), values);
	}

	protected _updateDialog(values?, params?, callback?: Function) {
		this._onBeforeDialog();

		this._dialog.update({
			target: this._feature || null,
			form: this._form,
			id: this._dialogId,
			onSubmit: (dialogRef, values_) => {
				// console.warn(values);
				this._updateAction(dialogRef, params || values, values_, callback);
			},
			values
		});
	}

	protected _updateAction(dialogRef: MatDialogRef<any>, params: {}, values: {}, callback?: Function) {
		this._form.freeze();

		this._getApi('_$update')
			.request(this._getParams(params), values)
			.subscribe({
				next: updatedItem => {
					dialogRef.close();

					this._toast.medium({
						id: 'updated',
						body: this._text.get('updated'),
						icon: 'icon24:check'
					});

					callback && callback(updatedItem);
					this.changed();
				},
				error: this._selfErrorCatchFunction
			});
	}

	//////////////////////////////
	// ROUTE

	protected _routeCreate() {
		if (!this._permit('write')) {
			return;
		}
		this._route.move$('./create');
	}

	protected _routeUpdate(values: {}, params?: {}) {
		if (!this._permit('write')) {
			return;
		}

		const primaryKey = values[this._primaryKeyName];
		this._route.move$('./updates/' + primaryKey);
	}

	protected _deleteAction(params: Record<string, string | number>) {
		return this._getApi('_$delete').request(this._getParams(params))
			.pipe(
				tap(response => {
					this._toast.high({
						id: 'deleted',
						body: this._text.get('deleted'),
						icon: 'icon24:trash'
					});

					this.changed();
				})
			);
	}

	/**
	 * 컴포넌트 연동 설정
	 * @param option
	 */
	with(option: ControlOption) {
		[
			'form',
			'params',
			'primaryKeyName',
			'paramsName',
			'beforeDialog',
			'permissionPrefix',
			'dialogId'
		].forEach(key => {
			const value = option[key];
			if (value !== undefined) {
				this['_' + key] = value;
			}
		});

		if (!this._primaryKeyName) {
			this._primaryKeyName = this._paramsName;
		}

		if (!this._dialogId) {
			const path     = this._route.path;
			this._dialogId =
				(path.startsWith('/') ? path.slice(1, path.length) : path).replace(/\//g, '-') + '-dialog';
		}

		const {service} = option as ControlOptionService;
		const apiFrom   = service || option as ControlOptionSpreadApi;

		this._$create  = apiFrom.$create;
		this._$read    = apiFrom.$read;
		this._$reads   = apiFrom.$reads;
		this._$update  = apiFrom.$update;
		this.$updates  = apiFrom.$updates;
		this._$delete  = apiFrom.$delete;
		this._$deletes = apiFrom.$deletes || apiFrom.$reset;

		return this;
	}

	/**
	 * 컴포넌트 연동 해제
	 */
	destroy() {
		this._modal.close('dbRestore');
	}

	/**
	 * 등록 API 설정
	 * @param api
	 */
	setCreateApi(api: Api) {
		this._$create = api;
		return this;
	}

	/**
	 * 주소 인자 설정
	 * @param key
	 * @param value
	 */
	setParam(key: string, value: string | number) {
		const {_params} = this;
		if (_params) {
			_params[key] = value;
		} else {
			this._params = {[key]: value};
		}
		return this;
	}

	/**
	 * 기능 설정
	 * @param feature
	 */
	setFeature(feature: string) {
		// this._feature = _UPPER_SNAKE_CASE.test(feature) ?
		// 	this._translate.define(feature) :
		// 	feature;
		//
		this._feature = this._translate.define(feature);
		return this;
	}

	/**
	 * 변경 처리
	 */
	changed() {
		this.changed$.next();
		this._route.refresh();
	}

	/**
	 * 등록
	 */
	create() {
		if (!this._permit('write')) {
			return;
		}

		this._onBeforeDialog();

		this._dialog.create({
			target: this._feature || null,
			form: this._form.asCreate(),
			id: this._dialogId,
			onSubmit: this._selfCreateAction
		});
	}


	update$(values?: any, params?: object) {
		return new Observable(observer => {
			if (!this._permit('write')) {
				return observer.error($withErrorMessage);
			}

			const observerNext = (item) => {
				observer.next(item);
				observer.complete();
			};

			const {_$read} = this;

			if (_$read) {
				_$read.request(this._getParams({
						[this._paramsName]: values[this._primaryKeyName]
					}),
					null,
					{cache: false} as any
				)
					.subscribe(response => {
						this._updateDialog({
								// sometimes missing sequence
								...values,
								...response
							},
							params,
							observerNext
						);
					});
			} else {
				this._updateDialog(
					values,
					params,
					observerNext
				);
			}
		});
	}


	/**
	 * 비동기 삭제
	 * @param params
	 * @param target
	 * @param content
	 */
	delete$<TParams extends ControlDeleteParams>(params: TParams, target?: string, content?: string): Observable<any> {
		if (!this._permit('delete')) {
			return throwError($withErrorMessage);
		}

		if (typeof params !== 'object') {
			params = {
				[this._primaryKeyName]: params
			} as TParams;
		}

		const subject = new Subject<void>();

		this._dialog.confirmDelete(
			() => {
				this._deleteAction(params as any)
					.subscribe({
						next: response => {
							subject.next();
						},
						error: this._selfErrorCatchFunction
					});
			},
			target,
			content
		);

		return subject;
	}

	/**
	 * 다중 삭제
	 * @param params
	 * @param target
	 * @param content
	 */
	deletes(params: ControlDeleteParams | Array<ControlDeleteParams>, target?: string, content?: string) {
		if (!this._permit('delete')) {
			return;
		}

		if (!Array.isArray(params)) {
			params = [params];
		}

		const {_primaryKeyName} = this;

		params = params
			.map(_params => {
				if (typeof _params !== 'object') {
					return {
						[_primaryKeyName]: _params
					};
				}
				return _params[_primaryKeyName];
			})
			.join(',');

		console.warn(params);

		this._dialog.confirmDeletes(
			() => {
				this._deleteAction(params as any).subscribe();
			},
			target,
			content
		);
	}

	/**
	 * 비동기 다중 삭제
	 * @param params
	 * @param target
	 * @param content
	 */
	deletes$(params: ControlDeleteParams | Array<ControlDeleteParams>, target?: string, content?: string) {
		if (!this._permit('delete')) {
			return;
		}

		if (!Array.isArray(params)) {
			params = [params];
		}

		const {_primaryKeyName} = this;

		params = params
			.map(_params => {
				if (typeof _params !== 'object') {
					return {
						[_primaryKeyName]: _params
					};
				}

				return _params[_primaryKeyName];
			})
			.join(',');

		const subject = new Subject<void>();

		this._dialog.confirmDeletes(
			() => {
				this._deleteAction(params as any)
					.subscribe({
						next: response => {
							subject.next();
						},
						error: this._selfErrorCatchFunction
					});
			},
			target,
			content
		);

		return subject;
	}


	/**
	 * 모두 삭제
	 */
	deleteAll() {
		if (!this._permit('delete')) {
			return throwError($withErrorMessage);
		}
		this._deleteAllDialog();
	}

	_deleteAllDialog() {
		this._dialog.confirmDelete(() => {
			this._deleteAllAction();
		});
	}

	_deleteAllAction() {
		return this._getApi('_$deletes').request()
			.subscribe(response => {
				this._toast.high({
					body: this._text.get('deletedAll'),
					icon: 'icon24:trash'
				});
				this.changed();
			});
	}

	/**
	 * 일괄 처리
	 */
	restore() {
		const {_core} = this;
		if (!_core.includes(DB_RESTORE)) {
			return console.debug('DB_RESTORE not available');
		}

		const {_$reads, $updates} = this;

		if (!_$reads && $updates) {
			return new RangeError(`Does not has Read many & Update many API`);
		}

		this._modal.sidebar(
			'dbRestore',
			_core.import<DbRestoreComponent>(DB_RESTORE, 'restore'),
			{
				data: {
					defined: this._feature,
					exportApi$: _$reads.get('.csv') as HttpRequester,
					importApi$: $updates as HttpRequester
				}
			}
		);
	}

	/**
	 * @deprecated update$() 사용
	 */
	update(values?: {}, params?: {}) {
		this.update$(values, params).subscribe();
	}

	/**
	 * @deprecated delete$ 사용
	 */
	delete<TParams extends ControlDeleteParams>(params: TParams, target?: string, content?: string) {
		this.delete$(params, target, content).subscribe();
	}

}

@Injectable()
/**
 * CRUD 조작 공통화 서비스
 */
export class SelfControlService extends ControlOperator {
	/**
	 * 추가 서비스 생성
	 * @param option
	 */
	spawn(option?: ControlOption): ControlOperator {
		const child = new ControlOperator(this._injector);
		return option ?
			child.with(option) :
			child;
	}
}
